import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TaxModalConfig, TaxModalPriceRows } from '../utils/taxInfoModal';
import './TaxInfoModal.css';

export type TaxInfoModalProps = {
  open: boolean;
  onClose: () => void;
  config: TaxModalConfig;
  prices?: TaxModalPriceRows;
};

export function TaxInfoModal({ open, onClose, config, prices }: TaxInfoModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeLabel = t('site.taxModalClose', { defaultValue: 'Close' });

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onKey]);

  useEffect(() => {
    if (!open) return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
    );
    const items = Array.from(focusable);
    items[0]?.focus();

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    root.addEventListener('keydown', onTab);
    return () => root.removeEventListener('keydown', onTab);
  }, [open]);

  if (!open) return null;

  const showCurrent =
    config.showCurrentPrice
    && Boolean(prices?.currentPrice);
  const showCompare =
    config.showCompareAt
    && Boolean(prices?.compareAtPrice);
  const showLow =
    config.showPriceLow30d
    && Boolean(prices?.priceLow30d);
  const bodyParagraphs = String(config.body || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const modal = (
    <div
      className="tax-info-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="tax-info-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tax-info-modal__head">
          <h2 id={titleId} className="tax-info-modal__title">
            {config.title}
          </h2>
          <button
            type="button"
            className="tax-info-modal__close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            ×
          </button>
        </div>

        <div className="tax-info-modal__body">
          {showCurrent ? (
            <div className="tax-info-modal__row tax-info-modal__row--current">
              <span className="tax-info-modal__row-label">
                {prices?.currentPriceLabel
                  || t('site.taxModalCurrentPrice', { defaultValue: 'Price' })}
              </span>
              <span className="tax-info-modal__row-value">
                <strong className="tax-info-modal__price">{prices?.currentPrice}</strong>
                {prices?.taxNote ? (
                  <span className="tax-info-modal__tax-note">{prices.taxNote}</span>
                ) : null}
              </span>
            </div>
          ) : null}

          {showCompare ? (
            <div className="tax-info-modal__row">
              <span className="tax-info-modal__row-label">
                {prices?.compareAtLabel
                  || t('site.taxModalCompareAt', { defaultValue: 'Original price' })}
              </span>
              <span className="tax-info-modal__row-value tax-info-modal__row-value--muted">
                {prices?.compareAtPrice}
              </span>
            </div>
          ) : null}

          {showLow ? (
            <div className="tax-info-modal__row">
              <span className="tax-info-modal__row-label">
                {prices?.priceLow30dLabel
                  || t('site.taxModalPriceLow30d', {
                    defaultValue: 'Lowest price in the last 30 days',
                  })}
              </span>
              <span className="tax-info-modal__row-value tax-info-modal__row-value--muted">
                {prices?.priceLow30d}
              </span>
            </div>
          ) : null}

          {bodyParagraphs.length > 0 ? (
            <div className="tax-info-modal__copy">
              {bodyParagraphs.map((para, i) => (
                <p key={i} className="tax-info-modal__para">
                  {para}
                </p>
              ))}
            </div>
          ) : null}
        </div>

        {config.moreInfoUrl ? (
          <div className="tax-info-modal__foot">
            <a
              className="tax-info-modal__more"
              href={config.moreInfoUrl}
              target={config.moreInfoUrl.startsWith('/') ? undefined : '_blank'}
              rel={config.moreInfoUrl.startsWith('/') ? undefined : 'noopener noreferrer'}
            >
              {config.moreInfoLabel
                || t('site.taxModalMoreInfo', { defaultValue: 'More information' })}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
